<template>
  <div>
    <QuickstartCard v-if="$route.query.quickstart">
      <template #header>👋 Post your first job</template>
      <template #body>
        We recommend posting at least 3 jobs and 7 cities to get started. Some of our favorite job titles are:
        <ul class="list-disc pl-4 mt-4">
          <li>Experienced Real Estate Agent</li>
          <li>Real Estate Agent - Inbound Leads Division</li>
          <li>Newly Licensed Real Estate Agent</li>
        </ul>
      </template>
    </QuickstartCard>
    <div v-if="!$store.state.general.loadingSpinner" class="flex flex-col">
      <DataTable :columns="dtColumns" :rows="rows" :click-fn="viewJob" :sort="sort" @sort-updated="handleNewSort">
        <template #filters="slotProps">
          <div class="flex justify-between items-center mb-4">
            <JobListFilters :filters="filters" @update-filter="handleNewFilter" />
            <div class="flex justify-end">
              <div class="ml-2" data-intro="Click here to create a new job.">
                <router-link to="/jobs/new">
                  <TButtonSvg label="New" color="indigo">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" />
                      <circle cx="12" cy="12" r="9" />
                      <line x1="9" y1="12" x2="15" y2="12" />
                      <line x1="12" y1="9" x2="12" y2="15" />
                    </svg>
                  </TButtonSvg>
                </router-link>
              </div>
            </div>
          </div>
        </template>
        <template #td-cities="slotProps">
          <Badge v-for="city of slotProps.row.cities" :key="`${city.id}-${city.value}`" variant="city">
            {{ city.value }}
          </Badge>
        </template>
        <template #td-Status="slotProps">
          <Badge :variant="slotProps.row.Status === 'Active' ? 'status-active' : 'status-inactive'">
            {{ slotProps.row.Status }}
          </Badge>
        </template>
        <template #td-Title="slotProps">
          <div class="flex items-center">
            {{ slotProps.row.title }}
            <span
              v-if="slotProps.row.isStale"
              class="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800"
            >
              STALE
            </span>
          </div>
        </template>
      </DataTable>
    </div>

    <LoadingList />
  </div>
</template>

<script>
import LoadingList from '../shared/LoadingList.vue';
import DataTable from '../shared/DataTable/index.vue';
import JobListFilters from './JobListFilters.vue';
import { formatStandardDateTime } from '../../utilities/format.ts';
import { listJobs } from '../../api/jobs.ts';
import QuickstartCard from '../shared/QuickstartCard.vue';
import { Badge } from '@/components/ui/badge';

export default {
  components: {
    LoadingList,
    DataTable,
    JobListFilters,
    QuickstartCard,
    Badge,
  },
  data() {
    return {
      loaded: false,
      jobs: [],
      startEntry: 0,
      endEntry: 0,
      total: 0,
      totalActive: 0,
      totalInactive: 0,
      page: this.$route.query.page ? this.$route.query.page : 1,
    };
  },
  computed: {
    dtColumns() {
      return [
        {
          label: 'Title',
          key: 'Title',
          sortable: true,
        },
        {
          label: 'Status',
          key: 'Status',
          sortable: false,
        },
        {
          label: 'Cities',
          key: 'cities',
          sortable: false,
        },
        {
          label: 'Last Refreshed',
          key: 'lastRefreshedAt',
          formatter: formatStandardDateTime,
          sortable: true,
        },
        {
          label: 'Created',
          key: 'created',
          formatter: formatStandardDateTime,
          sortable: true,
        },
      ];
    },
    rows() {
      return this.jobs;
    },
    columns() {
      if (this.contacts.length > 0) {
        let columns = Object.keys(this.contacts[0]).filter(column => column != 'id');
        return columns;
      } else {
        return [];
      }
    },
    sort() {
      return {
        key: this.$route.query.sort,
        order: this.$route.query.order,
      };
    },
    filters() {
      const query = this.$route.query;

      let filters = [];

      filters.push({ key: 'status', value: query.status || 'All' });

      return filters;
    },
  },
  mounted() {
    this.getJobs();
  },
  methods: {
    async viewJob({ id }) {
      await this.$router.push({ name: 'JobDetail', params: { id } });
    },
    async getJobs() {
      this.$store.commit('general/showLoadingSpinner');
      const queryParams = {
        status: this.$route.query.status,
        sort: this.$route.query.sort,
        order: this.$route.query.order,
      };
      const jobs = await listJobs(queryParams);
      this.jobs = jobs.records.map(job => ({
        title: job.title,
        Status: job.status,
        cities: job.job_locations
          .map(location => ({
            id: location.id,
            value: `${location.city}, ${location.state}`,
            status: location.status,
          }))
          .filter(location => location.status === 'active'),
        created: job.created_at,
        lastRefreshedAt: job.job_details.find(detail => detail?.status === 'active')?.created_at || job.created_at,
        id: job.id,
        isStale: job['is_stale?'],
      }));
      this.totalActive = jobs.total_active;
      this.totalInactive = jobs.total_inactive;
      this.$store.commit('general/hideLoadingSpinner');
      this.loaded = true;
    },
    async handleNewSort({ key, order }) {
      await this.$router.push({
        path: '/jobs',
        query: { sort: key.toLowerCase(), order: order, status: this.$route.query.status },
      });
      this.getJobs();
    },
    async handleNewFilter(newFilters, e) {
      const query = {
        sort: 'created',
        order: 'desc',
        status: newFilters.status.value || 'All',
      };
      await this.$router.push({ path: '/jobs', query });
      await this.getJobs();
    },
  },
};
</script>
